import { create } from 'zustand';
import * as THREE from 'three';

export type TerrainFeatureType = 'water' | 'greenery' | 'park' | 'none';

export interface PaintedTerrainArea {
	id: string;
	type: TerrainFeatureType;
	points: THREE.Vector3[]; // Polygon points defining the painted area
	color: number; // Color for visualization
}

export interface LandmarkBuilding {
	uuid: string; // Building object UUID
	name: string;
	originalMaterial: THREE.Material | THREE.Material[]; // Store original material for restoration
}

interface ViewFeaturesStore {
	// Landmark buildings
	landmarks: Map<string, LandmarkBuilding>;
	addLandmark: (uuid: string, name: string, originalMaterial: THREE.Material | THREE.Material[]) => void;
	removeLandmark: (uuid: string) => void;
	clearLandmarks: () => void;
	isLandmark: (uuid: string) => boolean;
	
	// Painted terrain features
	paintedAreas: PaintedTerrainArea[];
	addPaintedArea: (area: PaintedTerrainArea) => void;
	removePaintedArea: (id: string) => void;
	clearPaintedAreas: () => void;
	
	// Paint mode state
	isPaintMode: boolean;
	setIsPaintMode: (enabled: boolean) => void;
	currentPaintType: TerrainFeatureType;
	setCurrentPaintType: (type: TerrainFeatureType) => void;
	paintPoints: THREE.Vector3[]; // Current painting stroke
	setPaintPoints: (points: THREE.Vector3[]) => void;
	addPaintPoint: (point: THREE.Vector3) => void;
	clearPaintPoints: () => void;
	
	// Landmark selection mode
	isLandmarkSelectionMode: boolean;
	setIsLandmarkSelectionMode: (enabled: boolean) => void;
	
	// Paint area deletion mode
	isPaintDeletionMode: boolean;
	setIsPaintDeletionMode: (enabled: boolean) => void;
	
	// Paint area editing state
	selectedPaintedAreaId: string | null;
	setSelectedPaintedAreaId: (id: string | null) => void;
	updatePaintedAreaPoints: (id: string, points: THREE.Vector3[]) => void;

	resetAllModes: () => void; // Helper to reset all modes
}

export const useViewFeaturesStore = create<ViewFeaturesStore>((set, get) => ({
	// Landmarks
	landmarks: new Map(),
	addLandmark: (uuid, name, originalMaterial) => {
		const landmarks = new Map(get().landmarks);
		landmarks.set(uuid, { uuid, name, originalMaterial });
		set({ landmarks });
	},
	removeLandmark: (uuid) => {
		const landmarks = new Map(get().landmarks);
		landmarks.delete(uuid);
		set({ landmarks });
	},
	clearLandmarks: () => {
		set({ landmarks: new Map() });
	},
	isLandmark: (uuid) => get().landmarks.has(uuid),
	
	// Painted areas
	paintedAreas: [],
	addPaintedArea: (area) => {
		set((state) => ({ 
			paintedAreas: [...state.paintedAreas, area] 
		}));
	},
	removePaintedArea: (id) => {
		set((state) => ({ 
			paintedAreas: state.paintedAreas.filter(area => area.id !== id) 
		}));
	},
	clearPaintedAreas: () => {
		set({ paintedAreas: [] });
	},
	
	// Paint mode
	isPaintMode: false,
	setIsPaintMode: (enabled) => {
		set({ isPaintMode: enabled });
	},
	currentPaintType: 'greenery',
	setCurrentPaintType: (type) => set({ currentPaintType: type }),
	paintPoints: [],
	setPaintPoints: (points) => set({ paintPoints: points }),
	addPaintPoint: (point) => set((state) => ({ 
		paintPoints: [...state.paintPoints, point] 
	})),
	clearPaintPoints: () => set({ paintPoints: [] }),
	
	// Landmark mode
	isLandmarkSelectionMode: false,
	setIsLandmarkSelectionMode: (enabled) => {
		set({ isLandmarkSelectionMode: enabled });
	},
	
	// Paint deletion mode
	isPaintDeletionMode: false,
	setIsPaintDeletionMode: (enabled) => {
		set({ isPaintDeletionMode: enabled });
	},
	
	// Paint area editing
	selectedPaintedAreaId: null,
	setSelectedPaintedAreaId: (id) => {
		set({ selectedPaintedAreaId: id });
	},
	updatePaintedAreaPoints: (id, points) => {
		set((state) => ({
			paintedAreas: state.paintedAreas.map(area =>
				area.id === id ? { ...area, points } : area
			)
		}));
	},
	resetAllModes: () => {
		set({
			isLandmarkSelectionMode: false,
			isPaintMode: false,
			isPaintDeletionMode: false,
		});
	},
}));
